"use client";

import { useQueryClient } from "@tanstack/react-query";
import {
  projectSearchesQueryKey,
  savedSearchQueryKey,
  useCreateSavedSearch,
} from "@/hooks/use-search";
import type { SearchRequestSchema } from "@/lib/api/model";

interface UseCreateSearchOptions {
  projectId: string | null;
}

interface CreateSearchArgs {
  name?: string;
  /** When set, server composes the seed user message and returns it as
   * `seeded_message` on the response. */
  similar_profile_ids?: string[];
  /** Pools the new search covers; omitted = the org's default-search pools. */
  pool_ids?: string[];
}

/**
 * Thin mutation wrapper around `POST /projects/{id}/searches`.
 *
 * @deprecated legacy assistant path (spec 2). Creates a *definition-less*
 * saved search that the assistant fills in afterwards (hero prompt / JD /
 * similar-profiles entry points). Searches started from filters go through
 * `useRunSearch().createAndRun`, which creates the search and starts its
 * first run in one request; the backend rejects `filters` on this route.
 *
 * The hook updates the react-query cache on success but does NOT navigate;
 * callers own routing so they can interleave other side-effects (e.g.
 * seeding the pending-NQL-message store) between create and `router.push`.
 */
export function useCreateSearch({ projectId }: UseCreateSearchOptions) {
  const queryClient = useQueryClient();
  const createSavedSearch = useCreateSavedSearch();

  const createSearch = async (args: CreateSearchArgs = {}) => {
    if (!projectId) {
      throw new Error("useCreateSearch: projectId is required");
    }

    const request: SearchRequestSchema = {
      name: args.name,
      similar_profile_ids: args.similar_profile_ids,
      pool_ids: args.pool_ids,
    };

    const data = await createSavedSearch.mutateAsync({
      projectId,
      request,
    });

    queryClient.setQueryData(savedSearchQueryKey(data.search.id), data);
    queryClient.invalidateQueries({
      queryKey: projectSearchesQueryKey(projectId),
    });

    return data;
  };

  return {
    createSearch,
    isPending: createSavedSearch.isPending,
  };
}
